import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";

// Types
export interface ReminderCalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  status: "pending" | "completed" | "overdue" | "snoozed";
  reminderType: string;
  assignedTo?: string;
  createdBy?: string;
}

export interface ReminderCalendarParams {
  startDate: string;
  endDate: string;
  searchTerm?: string;
  reminderType?: string;
  status?: string;
  userId?: string | number;
}

export interface ReminderCalendarState {
  loading: boolean;
  error: string | null;
  data: ReminderCalendarEvent[];
  filters: ReminderCalendarParams;
}

// Initial state
const initialState: ReminderCalendarState = {
  loading: false,
  error: null,
  data: [],
  filters: {
    startDate: "",
    endDate: "",
    searchTerm: undefined,
    reminderType: undefined,
    status: undefined,
    userId: undefined,
  },
};

// Slice
const reminderCalendarSlice = createSlice({
  name: "reminderCalendar",
  initialState,
  reducers: {
    // Request
    fetchReminderCalendarRequest: (
      state,
      action: PayloadAction<ReminderCalendarParams>
    ) => {
      state.loading = true;
      state.error = null;
      state.filters = action.payload;
    },

    // Success
    fetchReminderCalendarSuccess: (
      state,
      action: PayloadAction<ReminderCalendarEvent[]>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },

    // Failure
    fetchReminderCalendarFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Clear data
    clearReminderCalendar: (state) => {
      state.loading = false;
      state.error = null;
      state.data = [];
      state.filters = initialState.filters;
    },

    // Update filters
    updateCalendarFilters: (
      state,
      action: PayloadAction<Partial<ReminderCalendarParams>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
});

// Actions
export const {
  fetchReminderCalendarRequest,
  fetchReminderCalendarSuccess,
  fetchReminderCalendarFailure,
  clearReminderCalendar,
  updateCalendarFilters,
} = reminderCalendarSlice.actions;

// Selectors
export const selectReminderCalendar = (
  state: RootState & { reminderCalendar: ReminderCalendarState }
) => state.reminderCalendar;
export const selectReminderCalendarLoading = (
  state: RootState & { reminderCalendar: ReminderCalendarState }
) => state.reminderCalendar.loading;
export const selectReminderCalendarError = (
  state: RootState & { reminderCalendar: ReminderCalendarState }
) => state.reminderCalendar.error;
export const selectReminderCalendarData = (
  state: RootState & { reminderCalendar: ReminderCalendarState }
) => state.reminderCalendar.data;
export const selectReminderCalendarFilters = (
  state: RootState & { reminderCalendar: ReminderCalendarState }
) => state.reminderCalendar.filters;

export default reminderCalendarSlice.reducer;
